import React, { forwardRef } from 'react';
import Icon, { IconName } from './icons/Icon';
import './InputTextArea.css';

type InputTextAreaVariant = 'default' | 'ghost';

interface InputTextAreaProps {
  /** 見た目のバリエーション */
  variant?: InputTextAreaVariant;
  /** 左アイコンを表示するかどうか */
  hasLeftIcon?: boolean;
  /** 左アイコンの名前 */
  leftIcon?: IconName;
  /** プレースホルダーテキスト */
  placeholder?: string;
  /** デフォルト値 */
  defaultValue?: string;
  /** 値（制御されたコンポーネント用） */
  value?: string;
  /** 変更時のコールバック */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** フォーカス時のコールバック */
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** ブラー時のコールバック */
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** IME入力開始時のコールバック */
  onCompositionStart?: (event: React.CompositionEvent<HTMLTextAreaElement>) => void;
  /** IME入力終了時のコールバック */
  onCompositionEnd?: (event: React.CompositionEvent<HTMLTextAreaElement>) => void;
  /** 無効状態 */
  disabled?: boolean;
  /** エラー状態 */
  error?: boolean;
  /** 必須項目 */
  required?: boolean;
  /** 名前属性 */
  name?: string;
  /** ID属性 */
  id?: string;
  /** 自動フォーカス */
  autoFocus?: boolean;
  /** 読み取り専用 */
  readOnly?: boolean;
  /** 最大文字数 */
  maxLength?: number;
  /** 最小文字数 */
  minLength?: number;
  /** 行数 */
  rows?: number;
  /** 追加のクラス名 */
  className?: string;
  /** aria-label */
  'aria-label'?: string;
  /** aria-describedby */
  'aria-describedby'?: string;
}

const InputTextArea = forwardRef<HTMLTextAreaElement, InputTextAreaProps>(({
  variant = 'default',
  hasLeftIcon = false,
  leftIcon = 'user',
  placeholder,
  defaultValue,
  value,
  onChange,
  onFocus,
  onBlur,
  onCompositionStart,
  onCompositionEnd,
  disabled = false,
  error = false,
  required = false,
  name,
  id,
  autoFocus,
  readOnly,
  maxLength,
  minLength,
  rows = 4,
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}, ref) => {
  return (
    <div
      className={`input-textarea input-textarea--${variant} ${error ? 'input-textarea--error' : ''} ${disabled ? 'input-textarea--disabled' : ''} ${className}`}
    >
      <div className="input-textarea__state-layer">
        <div className="input-textarea__container">
          {hasLeftIcon && (
            <div className="input-textarea__icon">
              <Icon name={leftIcon} size={24} />
            </div>
          )}
          <textarea
            ref={ref}
            className="input-textarea__textarea text-body-medium"
            placeholder={placeholder}
            defaultValue={defaultValue}
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onCompositionStart={onCompositionStart}
            onCompositionEnd={onCompositionEnd}
            disabled={disabled}
            required={required}
            name={name}
            id={id}
            autoFocus={autoFocus}
            readOnly={readOnly}
            maxLength={maxLength}
            minLength={minLength}
            rows={rows}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={error}
          />
        </div>
      </div>
    </div>
  );
});

InputTextArea.displayName = 'InputTextArea';

export default InputTextArea;
